// Single source of truth for "is this partner signed in", and the only place
// that tears a session down.
//
// Previously auth state lived in three places at once — a cookie, localStorage
// and redux — and each of the four logout paths cleared a different subset, so
// which one ran decided what was left behind. The token itself is the only
// thing the API cares about (it is sent as a Bearer header), so its own `exp`
// claim is what we read here rather than a parallel cookie clock.

const TOKEN_KEY = "token";
const HOTEL_ID_KEY = "hotelId";

// Everything a signed-in partner accumulates. Listed explicitly so tearing a
// session down never reaches beyond our own keys the way localStorage.clear() did.
const SESSION_KEYS = [
    TOKEN_KEY,
    HOTEL_ID_KEY,
    "userId",
    "previousRequestIds",
    "escalatedRequestLevels",
];

const SESSION_STORAGE_KEYS = ["restrictedAccessUnlocked"];

// The token used to be mirrored into a cookie that the backend never read. We
// no longer write these, but existing browsers still hold them, so a logout has
// to expire them or a stale cookie outlives the session that created it.
const LEGACY_COOKIES = [TOKEN_KEY, HOTEL_ID_KEY];

// Routes that render fine signed out; redirecting away from them would trap the
// user in a loop.
export const PUBLIC_PATHS = ["/", "/signup", "/staffTask"];

export const isPublicPath = (pathname) => PUBLIC_PATHS.includes(pathname);

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getHotelId = () => localStorage.getItem(HOTEL_ID_KEY);

const decodeTokenPayload = (token) => {
    try {
        const payload = token.split(".")[1];
        if (!payload) {
            return null;
        }
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(base64));
    } catch (error) {
        return null;
    }
};

// Anything we cannot read an expiry out of counts as expired: the API rejects
// tokens with no `exp` claim, so trusting one here would just defer the failure
// to a 401 on the next request.
export const isTokenExpired = (token) => {
    const payload = decodeTokenPayload(token);
    if (!payload || typeof payload.exp !== "number") {
        return true;
    }
    return payload.exp * 1000 <= Date.now();
};

// Null when there is nothing to wait for: no token, or one we cannot read an
// expiry from (already treated as expired by the callers).
export const millisecondsUntilExpiry = (token) => {
    if (!token) {
        return null;
    }
    const payload = decodeTokenPayload(token);
    if (!payload || typeof payload.exp !== "number") {
        return null;
    }
    return Math.max(0, payload.exp * 1000 - Date.now());
};

export const hasValidSession = () => {
    const token = getToken();
    if (!token || !getHotelId()) {
        return false;
    }
    return !isTokenExpired(token);
};

const expireCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
};

export const clearSession = () => {
    SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
    SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
    LEGACY_COOKIES.forEach(expireCookie);
};

// Removing the token fires a storage event in every other tab on this origin,
// which is how they learn to sign out too — no extra broadcast key needed.
// A login sets the key rather than clearing it, so `newValue` tells the two apart.
export const isLogoutStorageEvent = (event) =>
    event.key === null || (event.key === TOKEN_KEY && !event.newValue);
